import * as React from "react";
import { createPortal } from "react-dom";
import { Icon } from "../actions/Icon";
import { IconButton } from "../actions/IconButton";

/**
 * Modal dialog (DS Dialog): 18px corners, Hind Bold title, optional status icon, right-aligned footer
 * actions and an optional panel-tinted `aside` band (e.g. the invite-link band under an invite form).
 */
export interface DialogProps {
  open?: boolean;
  onClose?: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Phosphor icon in the round badge */
  icon?: string;
  iconTone?: "success" | "accent" | "record";
  width?: number;
  /** "alertdialog" for destructive confirmations that interrupt the flow */
  role?: "dialog" | "alertdialog";
  /** Position inside the nearest positioned ancestor instead of the viewport */
  contained?: boolean;
  footer?: React.ReactNode;
  /** Band under the footer on the panel tint, separated by a hairline */
  aside?: React.ReactNode;
  children?: React.ReactNode;
}

export function Dialog({
  open = true,
  onClose,
  title,
  subtitle,
  icon,
  iconTone = "success",
  width,
  role = "dialog",
  contained,
  footer,
  aside,
  children,
}: DialogProps): JSX.Element | null {
  const titleId = React.useId();
  if (!open) return null;
  const node = (
    <div
      className={"gk-scrim" + (contained ? " gk-scrim--contained" : "")}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="gk-dialog" role={role} aria-modal="true" aria-labelledby={titleId} style={width ? { width } : undefined}>
        <div className="gk-dialog__head">
          {icon && (
            <span className={"gk-dialog__icon" + (iconTone !== "success" ? " gk-dialog__icon--" + iconTone : "")}>
              <Icon name={icon} size={22} />
            </span>
          )}
          <div className="gk-dialog__titles">
            <h2 className="gk-dialog__title" id={titleId}>
              {title}
            </h2>
            {subtitle && <div className="gk-dialog__sub">{subtitle}</div>}
          </div>
          {onClose && <IconButton icon="x" label="Close" variant="muted" size="sm" onClick={onClose} />}
        </div>
        {children}
        {footer && <div className="gk-dialog__foot">{footer}</div>}
        {aside && <div className="gk-dialog__aside">{aside}</div>}
      </div>
    </div>
  );
  // A viewport dialog is portalled to <body>: rendered in place, a transformed or overflow-clipped
  // ancestor (a lifting library card, a scrolling panel) would become its containing block and
  // clip or trap it. `contained` keeps it inside its positioned ancestor on purpose.
  if (contained || typeof document === "undefined") return node;
  return createPortal(node, document.body);
}
